import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { supabase, unwrap } from './supabase';
import type {
  AccessTemplateRow,
  AircraftPairingRow,
  AircraftRow,
  AircraftTypeRow,
  AlternatingPilotsRow,
  AmendmentLogRow,
  CarryStateRow,
  ChangeLogRow,
  DocumentRow,
  MemberModuleAccessRow,
  MemberRow,
  OrgRow,
  OverrideRow,
  PilotDirectoryRow,
  PilotRow,
  PreviousRotationRow,
  QualificationRow,
  ScheduleAssignmentRow,
  ScheduleRunRow,
  ScheduleVersionRow,
  SettingsRow,
  TransitionRow,
  UnavailabilityRow,
  UtilizationEntryRow,
  UtilizationPeriodRow,
} from './database.types';

/**
 * Query keys. Input tables are keyed by their table name so
 * useTableMutations can invalidate by prefix.
 */
export const qk = {
  org: (orgId: string) => ['org', orgId] as const,
  settings: (orgId: string) => ['settings', orgId] as const,
  members: (orgId: string) => ['members', orgId] as const,
  memberAccess: (orgId: string) => ['member_module_access', orgId] as const,
  accessTemplates: (orgId: string) => ['access_templates', orgId] as const,
  documents: (orgId: string) => ['documents', orgId] as const,
  aircraftTypes: (orgId: string) => ['aircraft_types', orgId] as const,
  aircraft: (orgId: string) => ['aircraft', orgId] as const,
  pilots: (orgId: string) => ['pilots', orgId] as const,
  pilotDirectory: (orgId: string) => ['pilot_directory', orgId] as const,
  qualifications: (orgId: string) => ['qualifications', orgId] as const,
  unavailability: (orgId: string) => ['unavailability', orgId] as const,
  overrides: (orgId: string) => ['overrides', orgId] as const,
  transitions: (orgId: string) => ['transitions', orgId] as const,
  pairings: (orgId: string) => ['aircraft_pairings', orgId] as const,
  alternating: (orgId: string) => ['alternating_pilots', orgId] as const,
  previousRotations: (orgId: string) => ['previous_rotations', orgId] as const,
  carryState: (orgId: string) => ['carry_state', orgId] as const,
  utilizationPeriods: (orgId: string) => ['utilization_periods', orgId] as const,
  utilizationEntries: (orgId: string) => ['utilization_entries', orgId] as const,
  changeLog: (orgId: string, scope: string) => ['change_log', orgId, scope] as const,
  changesSince: (orgId: string, since: string | null) => ['changes_since', orgId, since] as const,
  amendmentLog: (orgId: string, versionId: string | null) => ['amendment_log', orgId, versionId] as const,
  version: (id: string | null) => ['schedule_version', id] as const,
  versions: (orgId: string) => ['schedule_versions', orgId] as const,
  run: (id: string | null) => ['schedule_run', id] as const,
  assignments: (runId: string | null) => ['schedule_assignments', runId] as const,
};

export function useOrg(orgId: string, initial?: OrgRow): UseQueryResult<OrgRow> {
  return useQuery({
    queryKey: qk.org(orgId),
    queryFn: async () => unwrap(await supabase.from('orgs').select('*').eq('id', orgId).single()),
    initialData: initial,
    staleTime: 5_000,
  });
}

export function useSettings(orgId: string, enabled = true): UseQueryResult<SettingsRow | null> {
  return useQuery({
    queryKey: qk.settings(orgId),
    enabled,
    queryFn: async () => {
      const res = await supabase.from('settings').select('*').eq('org_id', orgId).maybeSingle();
      if (res.error) throw new Error(res.error.message);
      return res.data;
    },
  });
}

export function useMembers(orgId: string): UseQueryResult<MemberRow[]> {
  return useQuery({
    queryKey: qk.members(orgId),
    queryFn: async () => unwrap(await supabase.from('members').select('*').eq('org_id', orgId).order('email')),
  });
}

export function useMemberAccess(orgId: string): UseQueryResult<MemberModuleAccessRow[]> {
  return useQuery({
    queryKey: qk.memberAccess(orgId),
    queryFn: async () => unwrap(await supabase.from('member_module_access').select('*').eq('org_id', orgId)),
  });
}

export function useAccessTemplates(orgId: string): UseQueryResult<AccessTemplateRow[]> {
  return useQuery({
    queryKey: qk.accessTemplates(orgId),
    queryFn: async () => unwrap(await supabase.from('access_templates').select('*').eq('org_id', orgId).order('sort').order('label')),
  });
}

export function useDocuments(orgId: string): UseQueryResult<DocumentRow[]> {
  return useQuery({
    queryKey: qk.documents(orgId),
    queryFn: () => liveRows(supabase.from('documents').select('*').eq('org_id', orgId).is('deleted_at', null).order('category').order('sort').order('title')),
  });
}

async function liveRows<T>(p: PromiseLike<{ data: T[] | null; error: { message: string } | null }>): Promise<T[]> {
  return unwrap(await p);
}

export function useAircraftTypes(orgId: string): UseQueryResult<AircraftTypeRow[]> {
  return useQuery({
    queryKey: qk.aircraftTypes(orgId),
    queryFn: () => liveRows(supabase.from('aircraft_types').select('*').eq('org_id', orgId).is('deleted_at', null).order('code')),
  });
}

export function useAircraft(orgId: string): UseQueryResult<AircraftRow[]> {
  return useQuery({
    queryKey: qk.aircraft(orgId),
    queryFn: () => liveRows(supabase.from('aircraft').select('*').eq('org_id', orgId).is('deleted_at', null).order('tail')),
  });
}

/** Full pilot records: schedulers only (RLS returns nothing to viewers). */
export function usePilots(orgId: string, enabled = true): UseQueryResult<PilotRow[]> {
  return useQuery({
    queryKey: qk.pilots(orgId),
    enabled,
    queryFn: () =>
      liveRows(supabase.from('pilots').select('*').eq('org_id', orgId).is('deleted_at', null).order('last_name').order('first_name')),
  });
}

/** Names and codes for everyone, including viewers. */
export function usePilotDirectory(orgId: string): UseQueryResult<PilotDirectoryRow[]> {
  return useQuery({
    queryKey: qk.pilotDirectory(orgId),
    queryFn: () => liveRows(supabase.from('v_pilot_directory').select('*').eq('org_id', orgId).order('last_name').order('first_name')),
  });
}

export function useQualifications(orgId: string): UseQueryResult<QualificationRow[]> {
  return useQuery({
    queryKey: qk.qualifications(orgId),
    queryFn: () => liveRows(supabase.from('qualifications').select('*').eq('org_id', orgId).is('deleted_at', null).order('created_at')),
  });
}

export function useUnavailability(orgId: string): UseQueryResult<UnavailabilityRow[]> {
  return useQuery({
    queryKey: qk.unavailability(orgId),
    queryFn: () => liveRows(supabase.from('unavailability').select('*').eq('org_id', orgId).is('deleted_at', null).order('start_date')),
  });
}

export function useOverrides(orgId: string): UseQueryResult<OverrideRow[]> {
  return useQuery({
    queryKey: qk.overrides(orgId),
    queryFn: () => liveRows(supabase.from('overrides').select('*').eq('org_id', orgId).is('deleted_at', null).order('rotation_start')),
  });
}

export function useTransitions(orgId: string): UseQueryResult<TransitionRow[]> {
  return useQuery({
    queryKey: qk.transitions(orgId),
    queryFn: () => liveRows(supabase.from('transitions').select('*').eq('org_id', orgId).is('deleted_at', null).order('effective_date')),
  });
}

export function usePairings(orgId: string): UseQueryResult<AircraftPairingRow[]> {
  return useQuery({
    queryKey: qk.pairings(orgId),
    queryFn: () => liveRows(supabase.from('aircraft_pairings').select('*').eq('org_id', orgId).is('deleted_at', null).order('start_date', { nullsFirst: true })),
  });
}

export function useAlternating(orgId: string): UseQueryResult<AlternatingPilotsRow[]> {
  return useQuery({
    queryKey: qk.alternating(orgId),
    queryFn: () => liveRows(supabase.from('alternating_pilots').select('*').eq('org_id', orgId).is('deleted_at', null).order('start_date', { nullsFirst: true })),
  });
}

export function usePreviousRotations(orgId: string): UseQueryResult<PreviousRotationRow[]> {
  return useQuery({
    queryKey: qk.previousRotations(orgId),
    queryFn: () => liveRows(supabase.from('previous_rotations').select('*').eq('org_id', orgId).is('deleted_at', null)),
  });
}

export function useCarryState(orgId: string): UseQueryResult<CarryStateRow[]> {
  return useQuery({
    queryKey: qk.carryState(orgId),
    queryFn: () => liveRows(supabase.from('carry_state').select('*').eq('org_id', orgId)),
  });
}

export function useUtilizationPeriods(orgId: string): UseQueryResult<UtilizationPeriodRow[]> {
  return useQuery({
    queryKey: qk.utilizationPeriods(orgId),
    queryFn: () => liveRows(supabase.from('utilization_periods').select('*').eq('org_id', orgId).is('deleted_at', null).order('period_start')),
  });
}

export function useUtilizationEntries(orgId: string): UseQueryResult<UtilizationEntryRow[]> {
  return useQuery({
    queryKey: qk.utilizationEntries(orgId),
    queryFn: () => liveRows(supabase.from('utilization_entries').select('*').eq('org_id', orgId).is('deleted_at', null)),
  });
}

/**
 * Change log. `scope` is either 'all' or a record id: entries whose row is
 * that record, or whose before/after payload names it as pilot_id (so a
 * pilot's page shows edits to their qualifications, time off, and so on).
 */
export function useChangeLog(orgId: string, scope: string = 'all', limit = 200): UseQueryResult<ChangeLogRow[]> {
  return useQuery({
    queryKey: qk.changeLog(orgId, scope),
    queryFn: async () => {
      let q = supabase.from('change_log').select('*').eq('org_id', orgId).order('ts', { ascending: false }).limit(limit);
      if (scope !== 'all') {
        q = q.or(`row_id.eq.${scope},after->>pilot_id.eq.${scope},before->>pilot_id.eq.${scope}`);
      }
      return unwrap(await q);
    },
  });
}

/** Number of input edits since the current draft was generated (spec 5.1 draft-status chip). */
export function useChangesSinceDraft(orgId: string, since: string | null, enabled = true): UseQueryResult<number> {
  return useQuery({
    queryKey: qk.changesSince(orgId, since),
    enabled: enabled && !!since,
    queryFn: async () => {
      if (!since) return 0;
      const res = await supabase
        .from('change_log')
        .select('id', { count: 'exact', head: true })
        .eq('org_id', orgId)
        .gt('ts', since)
        .not('table_name', 'in', '("members","utilization_periods","utilization_entries")');
      if (res.error) throw new Error(res.error.message);
      return res.count ?? 0;
    },
  });
}

export function useAmendmentLog(orgId: string, versionId: string | null): UseQueryResult<AmendmentLogRow[]> {
  return useQuery({
    queryKey: qk.amendmentLog(orgId, versionId),
    queryFn: async () => {
      let q = supabase.from('amendment_log').select('*').eq('org_id', orgId).order('ts', { ascending: false }).limit(500);
      if (versionId) q = q.eq('version_id', versionId);
      return unwrap(await q);
    },
  });
}

export function useVersion(versionId: string | null): UseQueryResult<ScheduleVersionRow | null> {
  return useQuery({
    queryKey: qk.version(versionId),
    enabled: !!versionId,
    queryFn: async () => {
      if (!versionId) return null;
      return unwrap(await supabase.from('schedule_versions').select('*').eq('id', versionId).single());
    },
  });
}

export function useVersions(orgId: string): UseQueryResult<ScheduleVersionRow[]> {
  return useQuery({
    queryKey: qk.versions(orgId),
    queryFn: () => liveRows(supabase.from('schedule_versions').select('*').eq('org_id', orgId).order('version_no', { ascending: false })),
  });
}

/** A run without its snapshot (the snapshot can be large; fetch it separately when needed). */
export type RunSummary = Omit<ScheduleRunRow, 'input_snapshot'>;

export function useRun(runId: string | null, enabled = true): UseQueryResult<RunSummary | null> {
  return useQuery({
    queryKey: qk.run(runId),
    enabled: enabled && !!runId,
    queryFn: async () => {
      if (!runId) return null;
      const res = await supabase
        .from('schedule_runs')
        .select('id, org_id, kind, input_hash, engine_version, seed, ok, errors, warnings, coverage_warnings, violations, utilization, carry_out, duration_ms, created_at, created_by')
        .eq('id', runId)
        .maybeSingle();
      if (res.error) throw new Error(res.error.message);
      return res.data as RunSummary | null;
    },
  });
}

export function useAssignments(runId: string | null): UseQueryResult<ScheduleAssignmentRow[]> {
  return useQuery({
    queryKey: qk.assignments(runId),
    enabled: !!runId,
    queryFn: async () => {
      if (!runId) return [];
      return unwrap(
        await supabase
          .from('schedule_assignments')
          .select('*')
          .eq('run_id', runId)
          .order('rotation_start')
          .order('aircraft_id')
          .order('seat')
          .limit(5000)
      );
    },
  });
}
